// ─── reportViewModel.js ───────────────────────────────────────────────────────
// Condition Report v3 — pure, derived view model. Reads project + areas +
// conditionBySubstrate and produces the ordered, RAG-driven structure the v3 Word
// engine renders. PURE: never mutates project.areas order (Excel indexes by
// position) — grouping/sorting happen on copies only.

import {
  CONDITION_TO_RAG,
  RAG_TOKENS,
  OVERALL_RAG_THRESHOLDS,
  RAG_DEFAULT_PRIORITY,
  PRIORITY_RANK,
  RAG_RECOMMENDATION,
  SITE_ITEMS,
  FLOOR_RECOMMENDATIONS,
  RAG_DEFAULT_TREATMENT,
  TREATMENT_BY_KEY,
  FULL_SPEC_TREATMENT,
  FULL_SPEC_TREATMENT_STAINED,
} from './areaConstants';
import { generateConditionNote, DEFECT_BY_ID, familyForSubstrate, resolveSubstrateOutcome } from './conditionLibrary';
import { normLabel } from './wordOoxml';

// Map an app condition key (poor/fair/good) → RAG (red/amber/green), else null.
export function conditionToRag(condition) {
  return CONDITION_TO_RAG[condition] || null;
}

// Count assessed substrates by RAG across all areas (§7.2). Only entries with a
// recognised condition are counted — a note without a rating is "seen, not rated".
// Excluded substrates (P3 — prefinished/unpainted/galvanised with no defects) are
// left out of the denominator entirely: they were looked at but are permanently
// out of scope, so counting them as "Good" would read as an artificially healthy
// site, and dropping them silently would misreport the total assessed.
export function computeStats(areas) {
  const stats = { red: 0, amber: 0, green: 0, total: 0 };
  (areas || []).forEach((area) => {
    const cbs = (area && area.conditionBySubstrate) || {};
    Object.keys(cbs).forEach((sub) => {
      const entry = cbs[sub];
      if (resolveSubstrateOutcome(entry).outcome === 'excluded') return;
      const rag = conditionToRag(entry && entry.condition);
      if (!rag) return;
      stats[rag] += 1;
      stats.total += 1;
    });
  });
  return stats;
}

// Count of substrates whose P3 outcome is 'excluded', across all areas. Kept
// separate from computeStats (whose {red,amber,green,total} shape other callers
// rely on) so the Good-news benefit count can include excluded surfaces as a
// genuine saving while still being able to explain the number (see
// surfacesNotNeedingRepaint below).
export function computeExcludedCount(areas) {
  let n = 0;
  (areas || []).forEach((area) => {
    const cbs = (area && area.conditionBySubstrate) || {};
    Object.keys(cbs).forEach((sub) => {
      if (resolveSubstrateOutcome(cbs[sub]).outcome === 'excluded') n += 1;
    });
  });
  return n;
}

// Count AREAS by their rollup RAG (worst substrate). Reinforces executive precision
// ("of 12 areas, 2 are Red …"). Distinct from computeStats, which counts substrates.
export function computeAreaStats(areas) {
  const stats = { red: 0, amber: 0, green: 0, total: 0 };
  (areas || []).forEach((area) => {
    const rag = rollupAreaRag(area);
    if (!rag) return;
    stats[rag] += 1;
    stats.total += 1;
  });
  return stats;
}

// Area RAG (Dashboard) = WORST substrate condition in that area (§7.1). null if
// none. Excluded substrates (P3) never contribute — an area that is otherwise
// entirely prefinished/unpainted/galvanised is not a painting job.
export function rollupAreaRag(area) {
  const cbs = (area && area.conditionBySubstrate) || {};
  let worst = null;
  let worstRank = 0;
  Object.keys(cbs).forEach((sub) => {
    const entry = cbs[sub];
    if (resolveSubstrateOutcome(entry).outcome === 'excluded') return;
    const rag = conditionToRag(entry && entry.condition);
    if (!rag) return;
    const rank = RAG_TOKENS[rag].rank;
    if (rank > worstRank) {
      worstRank = rank;
      worst = rag;
    }
  });
  return worst;
}

// Savings/benefit count for the executive "Good news" line: assessed surfaces NOT
// flagged for repaint — everything not rated poor/red (i.e. Fair + Good). Red is
// the only tier recommended for repaint (Repaint ASAP); Amber is worth considering
// but not essential, Green needs none at all — so neither needs repainting in the
// near term. Does NOT include
// excluded surfaces — see computeExcludedCount / buildReportViewModel's
// `benefitCount`, which folds them in at the composition level so the two counts
// stay separately explainable ("N surfaces don't need repainting, M of which are
// permanently excluded").
export function surfacesNotNeedingRepaint(stats) {
  const s = stats || {};
  return (s.green || 0) + (s.amber || 0);
}

// Overall Site Condition = proportional / threshold (§7.1). `override` (a RAG key)
// is the manual escape hatch and wins when set. null if nothing assessed.
export function overallRag(stats, override) {
  if (override) return override;
  if (!stats || !stats.total) return null;
  const redShare = stats.red / stats.total;
  const redAmberShare = (stats.red + stats.amber) / stats.total;
  if (redShare >= OVERALL_RAG_THRESHOLDS.redShare) return 'red';
  if (redAmberShare >= OVERALL_RAG_THRESHOLDS.redAmberShare) return 'amber';
  return 'green';
}

// RAG-derived default (assessor may override priority per area — sort only).
export function defaultPriority(rag) {
  return RAG_DEFAULT_PRIORITY[rag] || null;
}
function priorityFor(area, rag) {
  return (area && area.priority) || defaultPriority(rag);
}

// The one recommended action for a rated substrate/area — no per-area override,
// no separate maintenance-strategy picker. See RAG_RECOMMENDATION in areaConstants.js.
function actionFor(rag) {
  return rag ? RAG_RECOMMENDATION[rag].action : '';
}
function treatmentLabelFor(key) {
  return (TREATMENT_BY_KEY[key] && TREATMENT_BY_KEY[key].label) || '';
}

// ── Treatment level (P4) — Option 1 (full spec) vs Option 2 (recommended) ─────
// Resolve the treatment for one rated substrate (assessor override → RAG default).
function treatmentFor(entry, rag) {
  return (entry && entry.treatment) || RAG_DEFAULT_TREATMENT[rag] || null;
}

// The Option 1 baseline for a substrate: full spec, unless it is excluded (in
// which case both options agree there is no work, so this returns null and the
// substrate never appears in the comparison). Honours FULL_SPEC_TREATMENT_STAINED
// for finish === 'stained' (timber play equipment, dividing fences).
function fullSpecFor(entry) {
  if (resolveSubstrateOutcome(entry).outcome === 'excluded') return null;
  return (entry && entry.finish === 'stained') ? FULL_SPEC_TREATMENT_STAINED : FULL_SPEC_TREATMENT;
}

// Option 1 vs Option 2 comparison. Returns ONLY substrates where the two differ —
// a site with no reduced scope produces an empty array and the report omits the
// Scope Options section entirely.
export function buildOptionComparison(areas) {
  const rows = [];
  (areas || []).forEach((area) => {
    const cbs = (area && area.conditionBySubstrate) || {};
    Object.keys(cbs).forEach((sub) => {
      const e = cbs[sub];
      const rag = conditionToRag(e && e.condition);
      if (!rag) return; // nothing to compare without a rating
      const fullKey = fullSpecFor(e);
      if (!fullKey) return; // excluded — both options agree
      const recKey = treatmentFor(e, rag);
      if (!recKey || recKey === fullKey) return; // no reduced scope — omitted
      rows.push({
        area: area.name || '',
        substrate: sub,
        rag,
        fullSpec: { key: fullKey, label: treatmentLabelFor(fullKey) },
        recommended: { key: recKey, label: treatmentLabelFor(recKey) },
        rationale: (e.treatmentNote || '').trim(),
      });
    });
  });
  return rows;
}
// An area is "assessed" if at least one NON-EXCLUDED substrate carries a
// recognised condition — used for the RAG-driven dashboard/outlook/recommendation
// views, where a fully-excluded area (e.g. bare brick throughout) has nothing to
// paint and so drops out entirely.
function isAssessed(area) {
  return rollupAreaRag(area) !== null;
}

// Register inclusion is broader than isAssessed: an excluded substrate still has
// something to report (its reason for needing no work), so an area made up
// ENTIRELY of excluded substrates must still appear in the Detailed Condition
// Register even though it drops out of the RAG-driven views above.
function hasRegisterContent(area) {
  const cbs = (area && area.conditionBySubstrate) || {};
  return Object.keys(cbs).some((sub) => {
    const e = cbs[sub];
    return e && (conditionToRag(e.condition) || (e.note && e.note.trim()));
  });
}

// Dashboard (page 2): one row per assessed area, sorted priority-desc (Red first).
// Builds NEW row objects and sorts a NEW array — the input `areas` is never mutated
// (Excel indexes areas by position).
export function buildDashboardRows(areas) {
  const rows = (areas || []).filter(isAssessed).map((area) => {
    const rag = rollupAreaRag(area);
    return {
      area: area.name || '',
      rag,
      priority: priorityFor(area, rag),
      action: actionFor(rag),
    };
  });
  return rows.sort((a, b) => (
    (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0)
    || (RAG_TOKENS[b.rag].rank - RAG_TOKENS[a.rag].rank)
  ));
}

// Outlook (final page): one row per assessed area, in source order. cycleYear is a
// forward-compat seed for a future 10-year forecast (§11.1) — null now.
export function buildOutlookRows(areas) {
  return (areas || []).filter(isAssessed).map((area) => {
    const rag = rollupAreaRag(area);
    return {
      area: area.name || '',
      rag,
      action: actionFor(rag),
      cycleYear: null,
    };
  });
}

// Key Risks (§7.3): rank distinct defect families by frequency, top 5. An override
// list (assessor-edited) wins verbatim. A 'scheme' outcome (P3 — sound coating,
// wrong colour) contributes a "Colour scheme non-compliance" risk label alongside
// the defect-tag risks — a real, currently-invisible finding in the source reports.
export function aggregateKeyRisks(areas, override) {
  if (Array.isArray(override) && override.length) {
    return override.map((label) => ({ label }));
  }
  const counts = {};
  let schemeCount = 0;
  (areas || []).forEach((area) => {
    const cbs = (area && area.conditionBySubstrate) || {};
    Object.keys(cbs).forEach((sub) => {
      const entry = cbs[sub];
      const defects = (entry && entry.defects) || [];
      defects.forEach((id) => { counts[id] = (counts[id] || 0) + 1; });
      if (resolveSubstrateOutcome(entry).outcome === 'scheme') schemeCount += 1;
    });
  });
  const list = Object.keys(counts)
    .map((id) => ({ id, label: (DEFECT_BY_ID[id] && DEFECT_BY_ID[id].label) || id, count: counts[id] }));
  if (schemeCount > 0) list.push({ id: 'colour_scheme', label: 'Colour scheme non-compliance', count: schemeCount });
  return list.sort((a, b) => b.count - a.count).slice(0, 5);
}

// Defect families present per RAG tier, counted by AREA (an area contributes at most
// once to each defect it carries, in its rollup tier). Sorted by frequency desc.
export function aggregateDefectsByTier(areas) {
  const out = { red: {}, amber: {}, green: {} };
  (areas || []).forEach((area) => {
    const rag = rollupAreaRag(area);
    if (!rag || !out[rag]) return;
    const cbs = area.conditionBySubstrate || {};
    const seen = new Set(); // defect ids counted once per area
    Object.keys(cbs).forEach((sub) => {
      ((cbs[sub] && cbs[sub].defects) || []).forEach((id) => {
        if (seen.has(id)) return;
        seen.add(id);
        out[rag][id] = (out[rag][id] || 0) + 1;
      });
    });
  });
  const toList = (m) => Object.keys(m)
    .map((id) => ({ id, label: (DEFECT_BY_ID[id] && DEFECT_BY_ID[id].label) || id, count: m[id] }))
    .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
  return { red: toList(out.red), amber: toList(out.amber), green: toList(out.green) };
}

// Escape a string for literal use inside a RegExp.
const reEscape = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// A condition note that opens by naming its own surface stutters against a card
// header that already names it ("Upstairs Foyer - Walls" over "Walls: paint is
// peeling…"). Strip that opening self-reference in both shapes the note can take:
// the label form ("Walls:", "Walls —") and the sentence-subject form ("Walls is a
// prefinished finish…"). Covers notes generated by generateConditionNote() and
// notes an earlier assessment stored with the prefix already baked in. Only a
// LEADING mention goes — the surface named mid-sentence is left alone.
export function stripSurfaceLead(note, ...names) {
  const original = (note || '').trim();
  if (!original) return '';
  // Longest name first, so 'Rendered Walls' wins over a bare 'Walls'.
  const candidates = [...new Set(names.filter(Boolean).map((n) => String(n).trim()))]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);
  for (const name of candidates) {
    const n = reEscape(name);
    const label = original.replace(new RegExp(`^${n}\\s*[:\\u2013\\u2014-]\\s*`, 'i'), '');
    // Dropping the subject of "Walls is prefinished Colorbond and in good condition."
    // leaves a dangling "and" that only parsed with the subject in front, so the
    // first clause is rejoined with a comma: "Prefinished Colorbond, in good
    // condition." Confined to the opening sentence — later "and in" is real prose.
    const stripped = (label === original
      ? original.replace(new RegExp(`^${n}\\s+is\\s+`, 'i'), '').replace(/^([^.]*?) and in /, '$1, in ')
      : label).trim();
    if (stripped === original) continue;
    // Only a note we actually opened up gets re-capitalised — a note left as it was
    // keeps the assessor's own casing.
    if (!stripped) return original; // never blank a note out entirely
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
  }
  return original;
}

// Detailed Condition Register (pages 3+): per-substrate rows grouped under each
// area (source order). A row is included if it has a recognised condition OR a note;
// notes fall back to the deterministic generateConditionNote() when not stored.
// Uses hasRegisterContent rather than isAssessed so an area made up entirely of
// excluded substrates still appears — see hasRegisterContent's comment.
export function buildRegister(areas) {
  return (areas || []).filter(hasRegisterContent).map((area) => {
    const cbs = area.conditionBySubstrate || {};
    const rows = Object.keys(cbs)
      .filter((sub) => {
        const e = cbs[sub];
        return e && (conditionToRag(e.condition) || (e.note && e.note.trim()));
      })
      .map((sub) => {
        const e = cbs[sub];
        const rag = conditionToRag(e.condition);
        const settings = (area.surfaceSettings && area.surfaceSettings[sub]) || {};
        const base = settings.baseSurface || sub;
        const { outcome, reason } = resolveSubstrateOutcome(e);
        const excluded = outcome === 'excluded';
        const treatment = (rag && !excluded) ? treatmentFor(e, rag) : null;

        // Attribute photos: collect every photo tagged to this substrate or its baseSurface.
        // No guessing — untagged photos never appear.
        const photos = [];
        const tagged = (area.photos || []).filter((p) => (p.url || p.thumbnailUrl)
          && (normLabel(p.substrate) === normLabel(sub) || normLabel(p.substrate) === normLabel(base)));
        // primary first (isPrimaryForSubstrate), else preserve encounter order
        tagged.sort((x, y) => (y.isPrimaryForSubstrate === true) - (x.isPrimaryForSubstrate === true));
        tagged.forEach((p) => {
          const url = p.url || p.thumbnailUrl;
          if (!photos.some((q) => q.url === url)) photos.push({ url });
        });

        return {
          substrate: sub,
          category: familyForSubstrate(sub, base, area.type),
          rag,
          note: stripSurfaceLead((e.note && e.note.trim()) || generateConditionNote(sub, e), sub, base),
          action: excluded ? 'No work required' : (rag ? actionFor(rag) : ''),
          priority: (rag && !excluded) ? priorityFor(area, rag) : null,
          outcome,
          reason: reason || null,
          treatment,
          treatmentLabel: treatment ? treatmentLabelFor(treatment) : '',
          photos,
        };
      });
    return { area: area.name || '', rag: rollupAreaRag(area), rows };
  });
}

// RAG tiers that represent outstanding painting work — the register rows a
// "priority areas only" Condition Report keeps. Green is serviceable, so it is
// carried by the Area Summary alone rather than a detail page of its own.
export const PRIORITY_RAGS = ['red', 'amber'];

// Narrow a built register to the areas where painting work is needed: Amber and
// Red rows only, and area groups that still hold at least one such row. Excluded
// substrates (P3 — prefinished/unpainted/galvanised) drop out whatever their
// rating: "No work required" is the opposite of the work this view is for.
// PURE — returns new groups/rows arrays; the full register is left intact so the
// same view model can render either report.
export function filterRegisterToPriority(register) {
  return (register || [])
    .map((group) => ({
      ...group,
      rows: (group.rows || []).filter((r) => r
        && r.outcome !== 'excluded'
        && PRIORITY_RAGS.includes(r.rag)),
    }))
    .filter((group) => group.rows.length);
}

// Priority-grouped recommendation: assessed areas bucketed by their rollup RAG,
// emitted in priority order (Repaint ASAP → Consider Painting → No Painting
// Required). A tier is only present when it has at least one area, so a clean site
// drops the Repaint ASAP block entirely. Area order within a tier follows source
// order (areas never sorted in place — Excel indexes by position).
export function buildRecommendationTiers(areas) {
  const byRag = { red: [], amber: [], green: [] };
  (areas || []).filter(isAssessed).forEach((area) => {
    const rag = rollupAreaRag(area);
    if (byRag[rag]) byRag[rag].push(area.name || '');
  });
  return ['red', 'amber', 'green']
    .filter((rag) => byRag[rag].length)
    .map((rag) => ({
      rag,
      verb: RAG_RECOMMENDATION[rag].action,
      lead: RAG_RECOMMENDATION[rag].lead,
      areas: byRag[rag],
    }));
}

// Internal Flooring (§4). Floor coverings are graded for replacement, NOT painted
// — deliberately excluded from computeStats so flooring condition never moves the
// painting RAG or the asset-summary counts. A centre with tired vinyl and sound
// paintwork must still read as a green painting job.
const FLOOR_RECOMMENDATION_LABEL = Object.fromEntries(FLOOR_RECOMMENDATIONS.map((r) => [r.key, r.label]));
export function buildFlooringRows(project) {
  const rows = Array.isArray(project && project.flooring) ? project.flooring : [];
  return rows
    .filter((r) => r && typeof r.product === 'string' && r.product.trim())
    .map((r) => {
      const rag = conditionToRag(r.condition);
      return {
        product: r.product.trim(),
        location: r.location || '',
        rag,
        ragLabel: rag ? RAG_TOKENS[rag].label : '',
        recommendation: FLOOR_RECOMMENDATION_LABEL[r.recommendation] || '',
        note: r.note || '',
        safetyRisk: !!r.safetyRisk,
        safetyNote: (r.safetyNote || '').trim(),
        photos: Array.isArray(r.photos) ? r.photos : [],
      };
    });
}

// Building repairs referred to the client (not Higgins scope). Collected across
// every area so the report can carry the standing "review prior to painting"
// clause with an actual list attached. Referral NEVER affects a substrate's RAG,
// the site rollup, key risks or the option comparison — it is an observation
// about the building, not about the coating.
export function buildReferralRows(areas) {
  const rows = [];
  (areas || []).forEach((area) => {
    const cbs = (area && area.conditionBySubstrate) || {};
    Object.keys(cbs).sort().forEach((sub) => {
      const e = cbs[sub];
      if (!e || !e.referral) return;
      const note = (e.referralNote && e.referralNote.trim()) || (e.note && e.note.trim()) || '';
      if (!note) return; // both referralNote and the substrate note are empty — nothing to report
      rows.push({ area: area.name || '', substrate: sub, note, rag: conditionToRag(e.condition) });
    });
  });
  return rows;
}

// Site Items (§5) → report rows. Items with nothing recorded are dropped, so a
// simple site produces a short section rather than eleven rows of "Nil".
const QTY_LABEL_BY_STATUS = {
  nil: 'Nil',
  client_direction: 'At client direction',
  by_others: 'Already fitted by others',
};
export function buildSiteItemRows(project) {
  const si = (project && project.siteItems) || {};
  const counts = [];
  const tables = [];

  SITE_ITEMS.forEach((item) => {
    const entry = si[item.key] || {};
    const status = entry.status || 'counted';

    if (item.kind === 'count') {
      const hasCount = status === 'counted' && typeof entry.count === 'number' && entry.count > 0;
      const hasNote = typeof entry.note === 'string' && entry.note.trim().length > 0;
      const photos = (Array.isArray(entry.photos) ? entry.photos : []).filter((p) => p && (p.url || p.thumbnailUrl));
      const recorded = status !== 'counted' || hasCount || hasNote || photos.length > 0;
      if (!recorded) return;

      const row = {
        label: item.label,
        qty: QTY_LABEL_BY_STATUS[status] || String(entry.count || 0),
        note: (entry.note || '').trim(),
      };
      if (item.markup) {
        row.markup = entry.perClientMarkup === true ? 'Yes' : entry.perClientMarkup === false ? 'No' : '—';
      }
      if (photos.length) row.photos = photos.map((p) => ({ url: p.url || p.thumbnailUrl }));
      counts.push(row);
    } else if (item.kind === 'rows') {
      const rows = (Array.isArray(entry.rows) ? entry.rows : []).filter((row) =>
        item.cols.some((c) => { const v = row[c.k]; return typeof v === 'string' ? v.trim().length > 0 : !!v; })
      );
      if (!rows.length) return;
      tables.push({ label: item.label, cols: item.cols, rows });
    }
  });

  return {
    counts,
    tables,
    representativeRequests: (si.representativeRequests || '').trim(),
  };
}

// ── Top-level composition ─────────────────────────────────────────────────────
export function buildReportViewModel(project, areas) {
  const p = project || {};
  const meta = p.reportMeta || {};
  const list = areas || [];
  const stats = computeStats(list);
  const excludedCount = computeExcludedCount(list);
  return {
    meta: {
      siteName: meta.siteName || p.projectName || p.clientName || 'Site',
      clientName: p.clientName || '',
      inspectionDate: meta.inspectionDate || p.date || '',
      assessorName: meta.assessorName || meta.higginsRep || '',
      clientContact: meta.clientContact || meta.recipientName || '',
      siteDirector: meta.siteDirector || '',
      sector: p.sector || meta.sector || null,
      clientLogo: p.clientLogo || meta.clientLogo || null,
    },
    stats,
    areaStats: computeAreaStats(list),
    overallRAG: overallRag(stats, meta.overallConditionOverride || p.overallConditionOverride),
    keyRisks: aggregateKeyRisks(list, meta.keyRisksOverride),
    // Excluded surfaces count as a genuine saving ("we're not charging you to
    // paint your brickwork") but stay explainable as their own number.
    excludedCount,
    benefitCount: surfacesNotNeedingRepaint(stats) + excludedCount,
    recommendationTiers: buildRecommendationTiers(list),
    defectsByTier: aggregateDefectsByTier(list),
    dashboardRows: buildDashboardRows(list),
    register: buildRegister(list),
    outlookRows: buildOutlookRows(list),
    siteItems: buildSiteItemRows(p),
    flooring: buildFlooringRows(p),
    optionComparison: buildOptionComparison(list),
    referrals: buildReferralRows(list),
  };
}
